/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Input,
  Box,
  FormControl,
  InputLabel,
  InputAdornment,
  Stack,
  Typography,
  Divider,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  Paper,
  Chip,
  Fade,
  Pagination,
  alpha,
  Skeleton,
  Tooltip,
  useTheme,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DateRangePicker } from '@mui/x-date-pickers-pro';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import {
  Cancel,
  Search,
  FilterList,
  ReceiptLong,
  TrendingUp,
  ContentCopy,
  CheckCircleOutline,
  Close,
  VerifiedOutlined,
  RefreshOutlined,
  ExpandMore,
} from '@mui/icons-material';
import { fCurrency } from '../utils/formatNumber';
import TransactionsCard from './components/TransactionCard';
import ModalC from './components/CModal';
import { hasPermission, isAdminOrOwner, PERMISSIONS } from '../utils/hasPermission';
import {
  completeTransaction,
  getExternalFundingTransaction,
  getExternalPurchaseTransaction,
  getTransactionsHistory,
  refundTransaction,
} from '../api/transactions.api';
import { getUserById } from '../api/users.api';

// ─── Enhanced TextValue Component ────────────────────────────────────
export const TextValue = ({ text, value, mono, copyable }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 1.2,
        px: 1.5,
        borderRadius: '10px',
        transition: 'background-color 0.15s',
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
      }}
    >
      <Typography fontSize="0.85rem" color="text.secondary" fontWeight={500}>
        {text}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          fontSize="0.9rem"
          fontWeight={600}
          color="text.primary"
          sx={{
            fontFamily: mono ? '"SF Mono", "Fira Code", monospace' : 'inherit',
            maxWidth: '260px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'right',
          }}
        >
          {value || '—'}
        </Typography>
        {copyable && value && (
          <Tooltip title={copied ? 'Copied!' : 'Copy'}>
            <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5 }}>
              {copied ? (
                <CheckCircleOutline sx={{ fontSize: 16, color: '#059669' }} />
              ) : (
                <ContentCopy sx={{ fontSize: 14, color: 'text.disabled' }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

// ─── API Response Viewer ─────────────────────────────────────────────
const ApiResponse = ({ response, requestPayload }) => {
  const [tab, setTab] = useState('request');
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  let reqData = requestPayload || response?.request_payload || null;
  if (!reqData && Array.isArray(response?.attemptLogs) && response.attemptLogs.length > 0) {
    reqData = response.attemptLogs[0]?.requestPayload || null;
  }

  let resData = response?.api_response || response?.vendorResponse || response;
  if (resData && typeof resData === 'object' && resData.api_response) {
    resData = resData.api_response;
  }

  const hasRequestData = Boolean(reqData && (typeof reqData !== 'object' || Object.keys(reqData).length > 0));
  const hasResponseData = Boolean(resData && (typeof resData !== 'object' || Object.keys(resData).length > 0));

  if (!hasRequestData && !hasResponseData) return null;

  const activeTab = tab === 'request' && !hasRequestData && hasResponseData ? 'response' : tab;
  const activeData = activeTab === 'request'
    ? (reqData || { message: 'No outbound request payload captured' })
    : (resData || { message: 'No vendor response captured' });

  let formatted = '';
  try {
    const data = typeof activeData === 'string' ? JSON.parse(activeData) : activeData;
    formatted = JSON.stringify(data, null, 2);
  } catch {
    formatted = String(activeData);
  }

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(formatted);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => setExpanded(!expanded)}
        sx={{
          cursor: 'pointer',
          py: 0.8,
          px: 1.5,
          borderRadius: '10px',
          bgcolor: '#F8FAFC',
          border: '1px solid #E2E8F0',
          '&:hover': { backgroundColor: '#F1F5F9' },
        }}
      >
        <Stack direction="row" spacing={0.8} alignItems="center" onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            variant={activeTab === 'request' ? 'contained' : 'text'}
            onClick={() => { setTab('request'); setExpanded(true); }}
            sx={{
              textTransform: 'none',
              fontSize: '0.74rem',
              fontWeight: 700,
              borderRadius: '8px',
              py: 0.3,
              px: 1,
              minWidth: 'auto',
              boxShadow: 'none',
              ...(activeTab === 'request'
                ? { bgcolor: '#0284C7', color: '#FFFFFF', '&:hover': { bgcolor: '#0369A1' } }
                : { color: 'text.secondary', '&:hover': { bgcolor: '#E2E8F0' } }),
            }}
          >
            Outbound Request
          </Button>
          <Button
            size="small"
            variant={activeTab === 'response' ? 'contained' : 'text'}
            onClick={() => { setTab('response'); setExpanded(true); }}
            sx={{
              textTransform: 'none',
              fontSize: '0.74rem',
              fontWeight: 700,
              borderRadius: '8px',
              py: 0.3,
              px: 1,
              minWidth: 'auto',
              boxShadow: 'none',
              ...(activeTab === 'response'
                ? { bgcolor: '#0284C7', color: '#FFFFFF', '&:hover': { bgcolor: '#0369A1' } }
                : { color: 'text.secondary', '&:hover': { bgcolor: '#E2E8F0' } }),
            }}
          >
            Vendor Response
          </Button>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={copied ? 'Copied!' : 'Copy active JSON'}>
            <IconButton size="small" onClick={handleCopy} sx={{ color: 'text.secondary' }}>
              {copied ? <CheckCircleOutline sx={{ fontSize: 16, color: '#10B981' }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <ExpandMore
            sx={{
              fontSize: 18,
              color: 'text.secondary',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </Stack>
      </Stack>
      {expanded && (
        <Fade in={expanded}>
          <Box
            sx={{
              mt: 0.8,
              p: 2,
              borderRadius: '10px',
              bgcolor: '#0F172A',
              color: '#E2E8F0',
              fontFamily: '"SF Mono", "Fira Code", Consolas, monospace',
              fontSize: '0.75rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '250px',
              overflowY: 'auto',
            }}
          >
            {formatted}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

// ─── Status Badge for Detail Modal ──────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = {
    success: { color: '#059669', bg: '#ECFDF5', icon: '✓' },
    pending: { color: '#D97706', bg: '#FFFBEB', icon: '⏳' },
    failed: { color: '#DC2626', bg: '#FEF2F2', icon: '✕' },
    reversed: { color: '#6366F1', bg: '#EEF2FF', icon: '↩' },
  };
  const s = config[status] || config.pending;

  return (
    <Chip
      label={status?.charAt(0).toUpperCase() + status?.slice(1)}
      sx={{
        fontWeight: 700,
        fontSize: '0.8rem',
        backgroundColor: s.bg,
        color: s.color,
        border: `1px solid ${alpha(s.color, 0.2)}`,
        borderRadius: '8px',
        height: 30,
        px: 1,
      }}
    />
  );
};

// ─── Transaction Type Config ─────────────────────────────────────────
const transactionTypeConfig = {
  WALLET_FUNDING: { label: 'Wallet Funding', color: '#2563EB' },
  ADMIN_WALLET_FUNDING: { label: 'Admin Funding', color: '#7C3AED' },
  WALLET_DEDUCTING: { label: 'Wallet Deduct', color: '#DC2626' },
  DATA_ORDER: { label: 'Data Order', color: '#0891B2' },
  API_DATA_ORDER: { label: 'API Data Order', color: '#0891B2' },
  AIRTIME_ORDER: { label: 'Airtime Order', color: '#059669' },
  API_AIRTIME_ORDER: { label: 'API Airtime Order', color: '#059669' },
  ELECTRICITY_ORDER: { label: 'Electricity', color: '#D97706' },
  API_ELECTRICITY_ORDER: { label: 'API Electricity', color: '#D97706' },
  CABLE_ORDER: { label: 'TV/Cable', color: '#9333EA' },
  API_CABLE_ORDER: { label: 'API TV/Cable', color: '#9333EA' },
  RESULT_CHECK_ORDER: { label: 'Result Check', color: '#6366F1' },
  API_RESULT_CHECK_ORDER: { label: 'API Result Check', color: '#6366F1' },
};

// ─── Filter Chip Component ──────────────────────────────────────────
const FilterChip = ({ label, active, onClick, onDelete, color }) => (
  <Chip
    label={label}
    onClick={onClick}
    onDelete={active ? onDelete : undefined}
    size="small"
    sx={{
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: '0.78rem',
      height: 32,
      transition: 'all 0.2s',
      backgroundColor: active ? alpha(color || '#2563EB', 0.1) : 'transparent',
      color: active ? color || '#2563EB' : 'text.secondary',
      border: `1px solid ${active ? alpha(color || '#2563EB', 0.3) : '#E2E8F0'}`,
      '&:hover': {
        backgroundColor: alpha(color || '#2563EB', 0.08),
        borderColor: alpha(color || '#2563EB', 0.3),
      },
      '& .MuiChip-deleteIcon': {
        color: color || '#2563EB',
        fontSize: 16,
        '&:hover': { color: alpha(color || '#2563EB', 0.7) },
      },
    }}
  />
);

// ─── Skeleton Loader ─────────────────────────────────────────────────
const TransactionSkeleton = () => (
  <Stack spacing={1}>
    {[...Array(6)].map((_, i) => (
      <Stack key={i} direction="row" alignItems="center" spacing={2} sx={{ px: 2.5, py: 2 }}>
        <Skeleton variant="circular" width={48} height={48} />
        <Stack flex={1} spacing={0.5}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="35%" height={16} />
        </Stack>
        <Stack alignItems="flex-end" spacing={0.5}>
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="rounded" width={60} height={22} sx={{ borderRadius: '6px' }} />
        </Stack>
      </Stack>
    ))}
  </Stack>
);

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const Transactions = () => {
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const filterUserId = queryParams.get('show');
  const isStaff = queryParams.get('staff');

  const [transactions, setTransactions] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filterTransactionType, setFilterTransactionType] = useState('');
  const [filterTransactionStatus, setFilterTransactionStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [value, setValue] = React.useState([null, null]);
  const [showFilters, setShowFilters] = useState(false);

  const { user, activeApp } = useSelector((state) => state.auth);
  const { users } = useSelector((state) => state.users);
  const canAccessLive = isAdminOrOwner(user, activeApp);

  const [fetchedUsers, setFetchedUsers] = useState({});
  const [loadingUser, setLoadingUser] = useState(false);

  useEffect(() => {
    const agentId = selected?.entityTypeId;
    if (agentId && !fetchedUsers[agentId]) {
      setLoadingUser(true);
      getUserById(agentId)
        .then((res) => {
          if (res.ok && res.data?.data) {
            setFetchedUsers((prev) => ({
              ...prev,
              [agentId]: res.data.data,
            }));
          }
        })
        .catch((err) => {
          console.error('Failed to fetch user:', err);
        })
        .finally(() => {
          setLoadingUser(false);
        });
    } else {
      setLoadingUser(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.entityTypeId]);

  useEffect(() => {
    if (filterUserId && !fetchedUsers[filterUserId]) {
      getUserById(filterUserId)
        .then((res) => {
          if (res.ok && res.data?.data) {
            setFetchedUsers((prev) => ({
              ...prev,
              [filterUserId]: res.data.data,
            }));
          }
        })
        .catch((err) => {
          console.error('Failed to fetch filter user:', err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUserId]);

  const [verifyLoading, setVerifyLoading] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [completionLoading, setCompletionLoading] = useState(false);

  const toggleModal = () => {
    if (showModal) {
      setPurchaseData(null);
      setFundingData(null);
    }
    setShowModal(!showModal);
  };

  const [fundingData, setFundingData] = useState(null);
  const [purchaseData, setPurchaseData] = useState(null);
  const [isManual, setIsManual] = useState(false);

  const eligible = selected && selected.transactionType.includes('ORDER');

  const externalPayment =
    selected && (selected?.transactionType.includes('ORDER') || selected?.transactionType === 'WALLET_FUNDING');

  const activeFilterCount = [filterTransactionType, filterTransactionStatus, isManual, value[0] && value[1]].filter(
    Boolean
  ).length;

  const getTransactions = useCallback(
    async (page) => {
      setLoading(true);

      const filterOptions = {
        page,
        filterTransactionType,
        searchQuery,
        isManual,
      };

      if (filterTransactionStatus) {
        filterOptions.filterTransactionStatus = filterTransactionStatus;
      }

      if (value[0] && value[1]) {
        filterOptions.startDate = value[0];
        filterOptions.endDate = value[1];
      }

      if (filterUserId && !isStaff) {
        filterOptions.filterUserId = filterUserId;
      }

      if (filterUserId && isStaff) {
        filterOptions.staffUserId = filterUserId;
      }

      try {
        const response = await getTransactionsHistory(filterOptions);
        if (response.data) {
          setTransactions(response.data.transactions);
          const limit = response.data.limit || 50;
          const calculatedTotalPages = response.data.totalCount ? Math.ceil(response.data.totalCount / limit) : 1;
          setTotalPages(response.data.pagination?.totalPages || response.data.totalPages || calculatedTotalPages);
          setTotalAmount(new Intl.NumberFormat().format(response.data.sumAmount || 0));
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
      }

      setLoading(false);
    },
    [filterTransactionType, filterTransactionStatus, filterUserId, isManual, isStaff, searchQuery, value]
  );

  useEffect(() => {
    getTransactions(currentPage);
  }, [currentPage, filterTransactionType, filterTransactionStatus, searchQuery, value, isManual, getTransactions]);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRefundUserWallet = async () => {
    setRefundLoading(true);

    const request = await refundTransaction(selected.id);
    if (request.ok) {
      toast.success(request.data.message);
      await getTransactions(currentPage);
      setRefundLoading(false);
      toggleModal();
      return;
    }

    toast.error(request.data ? request.data.message : 'Unable to complete your request');
    setRefundLoading(false);
  };

  const handleCompleteTransaction = async () => {
    setCompletionLoading(true);

    const request = await completeTransaction(selected.id, { status: 'success' });
    if (request.ok) {
      toast.success(request.data.message);
      await getTransactions(currentPage);
      setCompletionLoading(false);
      toggleModal();
      return;
    }

    toast.error(request.data ? request.data.message : 'Unable to complete your request');
    setRefundLoading(false);
  };

  const handleVerifyTransactionExternally = () => {
    if (selected?.transactionType === 'WALLET_FUNDING') {
      handleGetTransactionExternalFunding();
    } else {
      handleGetTransactionExternalPurchase();
    }
  };

  const handleGetTransactionExternalFunding = async () => {
    setVerifyLoading(true);

    const request = await getExternalFundingTransaction(selected.id);
    if (request.ok) {
      toast.success(request.data.message);
      setFundingData(request.data?.data);
      setVerifyLoading(false);
      return;
    }

    toast.error(request.data ? request.data.message : 'Unable to complete your request');
    setVerifyLoading(false);
  };

  const handleGetTransactionExternalPurchase = async () => {
    setVerifyLoading(true);

    const request = await getExternalPurchaseTransaction(selected.id);
    if (request.ok) {
      toast.success(request.data.message);
      setPurchaseData(request.data?.data);
      setVerifyLoading(false);
      return;
    }

    toast.error(request.data ? request.data.message : 'Unable to complete your request');
    setVerifyLoading(false);
  };

  const getAgentUser = (agentId) => {
    const cachedUser = fetchedUsers[agentId];
    if (cachedUser) {
      return cachedUser?.user?.username || agentId;
    }

    if (users?.length < 1) {
      return '';
    }

    const findUser = users.find((item) => item.id === agentId);
    return findUser?.username || '';
  };

  const handleClearFilters = () => {
    setFilterTransactionType('');
    setFilterTransactionStatus('');
    setIsManual(false);
    setValue([null, null]);
  };

  const selectedTypeConfig = selected
    ? transactionTypeConfig[selected.transactionType] || { label: selected.transactionType, color: '#64748B' }
    : null;

  return (
    <>
      {/* ─── Transaction Detail Drawer (Side Modal) ────────────────────────── */}
      <ModalC
        isOpen={showModal}
        setOpen={toggleModal}
        width={{ xs: '100%', sm: 500, md: 560 }}
        sx={{ p: 0 }}
      >
        <Stack sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
          {/* Modal Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: '1px solid #F1F5F9',
              position: 'sticky',
              top: 0,
              bgcolor: 'white',
              zIndex: 10,
            }}
          >
            <Typography fontWeight={700} fontSize="1.15rem">
              Transaction Details
            </Typography>
            <IconButton
              onClick={toggleModal}
              size="small"
              sx={{
                bgcolor: '#F1F5F9',
                '&:hover': { bgcolor: '#E2E8F0' },
              }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          {selected && (
            <Box sx={{ px: 3, py: 2.5 }}>
              {/* Type & Status Header */}
              <Stack
                alignItems="center"
                spacing={2}
                sx={{
                  py: 3,
                  px: 2,
                  borderRadius: '16px',
                  background: `linear-gradient(135deg, ${alpha(selectedTypeConfig.color, 0.06)} 0%, ${alpha(
                    selectedTypeConfig.color,
                    0.02
                  )} 100%)`,
                  border: `1px solid ${alpha(selectedTypeConfig.color, 0.1)}`,
                  mb: 2.5,
                }}
              >
                <Chip
                  label={selectedTypeConfig.label}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    bgcolor: alpha(selectedTypeConfig.color, 0.1),
                    color: selectedTypeConfig.color,
                    borderRadius: '8px',
                    height: 32,
                  }}
                />
                <Typography fontSize="1.8rem" fontWeight={800} color="text.primary">
                  ₦{fCurrency(selected.amount)}
                </Typography>
                <StatusBadge status={selected.status} />
                <Typography fontSize="0.82rem" color="text.secondary" fontWeight={500}>
                  {moment(selected.createdAt).tz('Africa/Lagos').format('MMMM DD, YYYY')} at{' '}
                  {moment(selected.createdAt).tz('Africa/Lagos').format('h:mm a')}
                </Typography>
              </Stack>

              {/* Action Buttons */}
              {eligible && (
                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                  {hasPermission(user, PERMISSIONS.TRANSACTIONS_REFUND) && (
                    <LoadingButton
                      loading={refundLoading}
                      onClick={handleRefundUserWallet}
                      color="error"
                      variant="outlined"
                      disabled={!!(selected && selected.refund)}
                      fullWidth
                      sx={{
                        borderRadius: '12px',
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      {selected && selected.refund ? '✓ Refunded' : 'Refund Customer'}
                    </LoadingButton>
                  )}
                  {selected &&
                    selected?.isManual &&
                    selected?.status === 'pending' &&
                    hasPermission(user, PERMISSIONS.TRANSACTIONS_MANUAL_UPDATE) && (
                      <LoadingButton
                        loading={completionLoading}
                        onClick={handleCompleteTransaction}
                        color="success"
                        variant="outlined"
                        disabled={!!(selected && selected.status === 'success')}
                        fullWidth
                        sx={{
                          borderRadius: '12px',
                          py: 1.2,
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        Complete Transaction
                      </LoadingButton>
                    )}
                </Stack>
              )}

              {/* Detail Rows */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '14px',
                  border: '1px solid #F1F5F9',
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                <TextValue
                  text="Agent"
                  value={
                    loadingUser ? (
                      <CircularProgress size={14} />
                    ) : (
                      getAgentUser(selected?.entityTypeId)
                    )
                  }
                />
                <Divider sx={{ mx: 1.5 }} />
                <TextValue text="Description" value={selected.description} />
                <Divider sx={{ mx: 1.5 }} />
                <TextValue text="Name" value={selected.name} />
                <Divider sx={{ mx: 1.5 }} />
                <TextValue text="Reference" value={selected.ref} mono copyable />
                {selected.providerSlug && (
                  <>
                    <Divider sx={{ mx: 1.5 }} />
                    <TextValue
                      text="Provider"
                      value={selected.providerId ? `${selected.providerSlug} (ID: ${selected.providerId})` : selected.providerSlug}
                    />
                  </>
                )}
                {selected.target && (
                  <>
                    <Divider sx={{ mx: 1.5 }} />
                    <TextValue text="Recipient / Target" value={selected.target} mono copyable />
                  </>
                )}
                {(selected.token || selected.details?.meter?.token || selected.details?.exam?.token) && (
                  <>
                    <Divider sx={{ mx: 1.5 }} />
                    <TextValue
                      text="Token / PIN"
                      value={selected.token || selected.details?.meter?.token || selected.details?.exam?.token}
                      mono
                      copyable
                    />
                  </>
                )}
                {selected.statusMessage && (
                  <>
                    <Divider sx={{ mx: 1.5 }} />
                    <TextValue text="Status Message" value={selected.statusMessage} />
                  </>
                )}
                {(selected.balanceBefore !== undefined && selected.balanceBefore !== null
                  ? true
                  : Boolean(selected.details && selected.details.balance)) && (
                  <>
                    <Divider sx={{ mx: 1.5 }} />
                    <TextValue
                      text="Balance Before"
                      value={`₦${fCurrency(selected.balanceBefore ?? selected.details?.balance?.before ?? 0)}`}
                    />
                    <Divider sx={{ mx: 1.5 }} />
                    <TextValue
                      text="Balance After"
                      value={`₦${fCurrency(selected.balanceAfter ?? selected.details?.balance?.after ?? 0)}`}
                    />
                  </>
                )}
                {(selected.performedBy || selected.details?.performedBy) && (
                  <>
                    <Divider sx={{ mx: 1.5 }} />
                    <TextValue
                      text="Processed By Staff/Admin"
                      value={`${(selected.performedBy || selected.details.performedBy).name || 'Admin'} (${(selected.performedBy || selected.details.performedBy).email || 'No email'}) [${(selected.performedBy || selected.details.performedBy).role || 'staff'}]`}
                    />
                  </>
                )}
              </Paper>

              {/* API Response & Provider Request */}
              <ApiResponse
                response={selected?.response_data}
                requestPayload={selected?.request_payload || selected?.response_data?.request_payload}
              />

              {/* External Verification */}
              {externalPayment && !fundingData && !purchaseData && (
                <LoadingButton
                  disabled={verifyLoading}
                  loading={verifyLoading}
                  onClick={handleVerifyTransactionExternally}
                  variant="outlined"
                  fullWidth
                  startIcon={<VerifiedOutlined />}
                  sx={{
                    mt: 2,
                    borderRadius: '12px',
                    py: 1.2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#E2E8F0',
                    color: 'text.primary',
                    '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
                  }}
                >
                  Verify Externally
                </LoadingButton>
              )}

              {/* External Funding Data */}
              {fundingData && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    borderRadius: '14px',
                    border: '1px solid #F1F5F9',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, bgcolor: '#F8FAFC', borderBottom: '1px solid #F1F5F9' }}>
                    <Typography fontWeight={700} fontSize="0.9rem">
                      External Funding Data
                    </Typography>
                  </Box>
                  <TextValue text="Payment Status" value={fundingData?.paymentStatus} />
                  <Divider sx={{ mx: 1.5 }} />
                  <TextValue text="Description" value={fundingData?.paymentDescription} />
                  <Divider sx={{ mx: 1.5 }} />
                  <TextValue text="Payment Method" value={fundingData?.paymentMethod} />
                  <Divider sx={{ mx: 1.5 }} />
                  <TextValue text="Reference" value={fundingData?.transactionReference} mono copyable />
                  <Divider sx={{ mx: 1.5 }} />
                  <TextValue text="Amount Paid" value={fundingData?.amountPaid} />
                </Paper>
              )}

              {/* External Purchase Data */}
              {purchaseData && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 2,
                    borderRadius: '14px',
                    border: '1px solid #F1F5F9',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, bgcolor: '#F8FAFC', borderBottom: '1px solid #F1F5F9' }}>
                    <Typography fontWeight={700} fontSize="0.9rem">
                      External Purchase Data
                    </Typography>
                  </Box>
                  <TextValue text="Payment Status" value={purchaseData?.paymentStatus} />
                  <Divider sx={{ mx: 1.5 }} />
                  <TextValue text="Description" value={purchaseData?.paymentDescription} />
                  <Divider sx={{ mx: 1.5 }} />
                  <TextValue text="Payment Method" value={purchaseData?.paymentMethod} />
                  <Divider sx={{ mx: 1.5 }} />
                  <TextValue text="Reference" value={purchaseData?.transactionReference} mono copyable />
                  <Divider sx={{ mx: 1.5 }} />
                  <TextValue text="Amount Paid" value={purchaseData?.amountPaid} />
                </Paper>
              )}
            </Box>
          )}
        </Stack>
      </ModalC>

      {/* ─── Main Content ──────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pb: 6, maxWidth: '1200px', mx: 'auto' }}>
        {/* Page Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3, mt: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
              }}
            >
              <ReceiptLong sx={{ color: 'white', fontSize: 22 }} />
            </Box>
            <Stack>
              <Typography fontWeight={800} fontSize="1.5rem" lineHeight={1.2}>
                Transactions
              </Typography>
              <Typography fontSize="0.82rem" color="text.secondary">
                Monitor and manage all transactions
              </Typography>
            </Stack>
          </Stack>

          {/* Live Stream Shortcut + Summary Card */}
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            {canAccessLive && (
              <Button
                component={RouterLink}
                to="/dashboard/transactions/live"
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1.2,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: alpha('#059669', 0.35),
                  bgcolor: '#ECFDF5',
                  color: '#059669',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    bgcolor: '#D1FAE5',
                    borderColor: '#059669',
                  },
                  '@keyframes txLiveDot': {
                    '0%': { boxShadow: '0 0 0 0 rgba(5, 150, 105, 0.55)' },
                    '70%': { boxShadow: '0 0 0 6px rgba(5, 150, 105, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(5, 150, 105, 0)' },
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#059669',
                    display: 'inline-block',
                    animation: 'txLiveDot 2s infinite',
                  }}
                />
                Live Transactions
              </Button>
            )}

            <Paper
              elevation={0}
              sx={{
                px: 2.5,
                py: 1.5,
                borderRadius: '14px',
                border: '1px solid #E2E8F0',
                background: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <TrendingUp sx={{ color: '#059669', fontSize: 20 }} />
              <Stack>
                <Typography
                  fontSize="0.72rem"
                  color="text.secondary"
                  fontWeight={500}
                  textTransform="uppercase"
                  letterSpacing={0.5}
                >
                  Total Amount
                </Typography>
                <Typography fontSize="1.15rem" fontWeight={800} color="#059669">
                  ₦{totalAmount}
                </Typography>
              </Stack>
            </Paper>
          </Stack>
        </Stack>

        {/* Search & Filter Bar */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            mb: 3,
            overflow: 'hidden',
          }}
        >
          {/* Search Row */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            spacing={2}
            sx={{ px: 2.5, py: 2 }}
          >
            <TextField
              placeholder="Search transactions..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.disabled', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: '#F8FAFC',
                  '& fieldset': { borderColor: '#E2E8F0' },
                  '&:hover fieldset': { borderColor: '#CBD5E1' },
                  '&.Mui-focused fieldset': { borderColor: '#2563EB', borderWidth: 1.5 },
                },
              }}
            />

            <Stack direction="row" alignItems="center" spacing={1.5}>
              <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{ start: 'Start', end: 'End' }}>
                <DateRangePicker
                  value={value}
                  slots={{ field: SingleInputDateRangeField }}
                  onChange={(newValue) => setValue(newValue)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        minWidth: 200,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          bgcolor: '#F8FAFC',
                          '& fieldset': { borderColor: '#E2E8F0' },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <Tooltip title={showFilters ? 'Hide filters' : 'Show filters'}>
                <IconButton
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    bgcolor: showFilters ? alpha('#2563EB', 0.1) : '#F8FAFC',
                    border: `1px solid ${showFilters ? alpha('#2563EB', 0.3) : '#E2E8F0'}`,
                    borderRadius: '12px',
                    color: showFilters ? '#2563EB' : 'text.secondary',
                    position: 'relative',
                    '&:hover': { bgcolor: alpha('#2563EB', 0.08) },
                  }}
                >
                  <FilterList sx={{ fontSize: 20 }} />
                  {activeFilterCount > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        bgcolor: '#2563EB',
                        color: 'white',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {activeFilterCount}
                    </Box>
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Expandable Filter Panel */}
          {showFilters && (
            <Fade in={showFilters}>
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  bgcolor: '#FAFBFC',
                  borderTop: '1px solid #F1F5F9',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ sm: 'center' }}
                  spacing={2}
                  flexWrap="wrap"
                >
                  {/* Transaction Type */}
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="transaction-type-label">Transaction Type</InputLabel>
                    <Select
                      labelId="transaction-type-label"
                      id="transaction-type-select"
                      value={filterTransactionType}
                      label="Transaction Type"
                      onChange={(event) => setFilterTransactionType(event.target.value)}
                      sx={{
                        borderRadius: '12px',
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#E2E8F0' },
                      }}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="WALLET_FUNDING">Wallet Funding</MenuItem>
                      <MenuItem value="ADMIN_WALLET_FUNDING">Admin Wallet Funding</MenuItem>
                      <MenuItem value="WALLET_DEDUCTING">Admin Wallet Deduct</MenuItem>
                      <MenuItem value="TRANSACTION_REFUND">Transaction Refund</MenuItem>
                      <MenuItem value="DATA_ORDER">Data Order</MenuItem>
                      <MenuItem value="API_DATA_ORDER">API Data Order</MenuItem>
                      <MenuItem value="AIRTIME_ORDER">Airtime Order</MenuItem>
                      <MenuItem value="API_AIRTIME_ORDER">API Airtime Order</MenuItem>
                      <MenuItem value="ELECTRICITY_ORDER">Electricity Payment</MenuItem>
                      <MenuItem value="API_ELECTRICITY_ORDER">API Electricity Payment</MenuItem>
                      <MenuItem value="CABLE_ORDER">TV Payment</MenuItem>
                      <MenuItem value="API_CABLE_ORDER">API TV Payment</MenuItem>
                      <MenuItem value="RESULT_CHECK_ORDER">Result Check Payment</MenuItem>
                      <MenuItem value="API_RESULT_CHECK_ORDER">API Result Check Payment</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Transaction Status */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="transaction-status-label">Status</InputLabel>
                    <Select
                      labelId="transaction-status-label"
                      id="transaction-status-select"
                      value={filterTransactionStatus}
                      label="Status"
                      onChange={(event) => setFilterTransactionStatus(event.target.value)}
                      sx={{
                        borderRadius: '12px',
                        bgcolor: 'white',
                        '& fieldset': { borderColor: '#E2E8F0' },
                      }}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="success">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#059669' }} />
                          <span>Success</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="pending">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#D97706' }} />
                          <span>Pending</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="failed">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#DC2626' }} />
                          <span>Failed</span>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="reversed">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6366F1' }} />
                          <span>Reversed</span>
                        </Stack>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Is Manual */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isManual}
                        onChange={(e) => setIsManual(e.target.checked)}
                        inputProps={{ 'aria-label': 'controlled' }}
                        size="small"
                        sx={{
                          color: '#CBD5E1',
                          '&.Mui-checked': { color: '#2563EB' },
                        }}
                      />
                    }
                    label={
                      <Typography fontSize="0.85rem" fontWeight={500}>
                        Manual Only
                      </Typography>
                    }
                    sx={{
                      ml: 0,
                      border: '1px solid #E2E8F0',
                      borderRadius: '12px',
                      px: 1.5,
                      py: 0.2,
                      bgcolor: isManual ? alpha('#2563EB', 0.04) : 'white',
                      borderColor: isManual ? alpha('#2563EB', 0.3) : '#E2E8F0',
                    }}
                  />

                  {/* Clear Filters */}
                  {activeFilterCount > 0 && (
                    <Button
                      startIcon={<RefreshOutlined sx={{ fontSize: 16 }} />}
                      onClick={handleClearFilters}
                      size="small"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        color: '#DC2626',
                        borderRadius: '12px',
                        '&:hover': { bgcolor: alpha('#DC2626', 0.05) },
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                </Stack>

                {/* Active Filter Chips */}
                {activeFilterCount > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    {filterTransactionType && (
                      <FilterChip
                        label={transactionTypeConfig[filterTransactionType]?.label || filterTransactionType}
                        active
                        color={transactionTypeConfig[filterTransactionType]?.color}
                        onDelete={() => setFilterTransactionType('')}
                      />
                    )}
                    {filterTransactionStatus && (
                      <FilterChip
                        label={`Status: ${filterTransactionStatus}`}
                        active
                        color={
                          filterTransactionStatus === 'success'
                            ? '#059669'
                            : filterTransactionStatus === 'failed'
                            ? '#DC2626'
                            : filterTransactionStatus === 'pending'
                            ? '#D97706'
                            : '#6366F1'
                        }
                        onDelete={() => setFilterTransactionStatus('')}
                      />
                    )}
                    {isManual && (
                      <FilterChip label="Manual" active color="#7C3AED" onDelete={() => setIsManual(false)} />
                    )}
                    {value[0] && value[1] && (
                      <FilterChip label="Date Range" active color="#0891B2" onDelete={() => setValue([null, null])} />
                    )}
                  </Stack>
                )}
              </Box>
            </Fade>
          )}
        </Paper>

        {/* ─── Transactions List ───────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '16px',
            border: '1px solid #E2E8F0',
            overflow: 'hidden',
            minHeight: 400,
          }}
        >
          {/* List Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              px: 2.5,
              py: 1.5,
              bgcolor: '#FAFBFC',
              borderBottom: '1px solid #F1F5F9',
            }}
          >
            <Typography fontSize="0.82rem" fontWeight={600} color="text.secondary">
              {loading ? 'Loading...' : `${transactions?.length || 0} transactions`}
            </Typography>
            <Typography fontSize="0.78rem" color="text.disabled">
              Page {currentPage} of {totalPages}
            </Typography>
          </Stack>

          {/* Transactions */}
          {loading ? (
            <TransactionSkeleton />
          ) : transactions && transactions.length > 0 ? (
            <Stack sx={{ py: 0.5 }}>
              {transactions.map((transaction, index) => (
                <Box key={transaction.id}>
                  <TransactionsCard
                    onClick={() => {
                      setSelected(transaction);
                      toggleModal();
                    }}
                    data={transaction}
                  />
                  {index < transactions.length - 1 && <Divider sx={{ mx: 2.5 }} />}
                </Box>
              ))}
            </Stack>
          ) : (
            <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 8 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '16px',
                  bgcolor: '#F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ReceiptLong sx={{ color: '#94A3B8', fontSize: 28 }} />
              </Box>
              <Stack alignItems="center" spacing={0.5}>
                <Typography fontWeight={600} color="text.secondary">
                  No transactions found
                </Typography>
                <Typography fontSize="0.85rem" color="text.disabled">
                  Try adjusting your search or filters
                </Typography>
              </Stack>
            </Stack>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Stack
              alignItems="center"
              sx={{
                py: 2,
                borderTop: '1px solid #F1F5F9',
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                siblingCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
                      color: 'white',
                      boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
                    },
                  },
                }}
              />
            </Stack>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default Transactions;
